/**
 * DownFacingAO — Cheap fake ambient-occlusion for automotive models.
 *
 * Darkens (or tints) surfaces whose world-space normal points downward.
 * This adds visual depth and ground-contact shadow to wheel arches,
 * bumper undersides, and engine bay recesses — without any ray-marching,
 * SSAO, or baked AO textures.
 *
 * Cost: 1 dot-product in vertex shader + 1 mix in fragment shader.
 *
 * Compatible with MeshStandardMaterial and MeshPhysicalMaterial.
 *
 * @example
 *   // Production: subtle black AO
 *   applyDownFacingAO(mat, { strength: 0.8, color: [0, 0, 0] });
 *
 *   // Debug: hot-pink tint to verify coverage
 *   applyDownFacingAO(mat, { strength: 1.0, color: [1, 0, 1] });
 *
 * @see docs/ims-engine-api/06-feature-color-system.md
 */

import {
  Mesh,
  MeshStandardMaterial,
  MeshPhysicalMaterial,
  type Material,
  type Object3D,
} from 'three';
import { extendMaterialShader } from './extendMaterialShader';

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

/** Options for the down-facing AO effect. */
export interface DownFacingAOOptions {
  /**
   * How strongly the AO tints affected regions.
   * 0 = no effect, 1 = fully coloured.
   * @default 0.8
   */
  strength?: number;
  /**
   * RGB tint colour applied to down-facing surfaces.
   * Use `[1, 0, 1]` (hot pink) for debugging.
   * @default [0, 0, 0] (black — standard AO)
   */
  color?: [number, number, number];
}

const MOD_KEY = 'downAO:v1';

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Patch a material with the down-facing AO effect.
 *
 * Idempotent: calling twice on the same material is a no-op
 * (guarded by `extendMaterialShader`'s key tracking).
 *
 * @param mat  Three.js material to patch.
 * @param opts Optional strength + debug colour overrides.
 */
export function applyDownFacingAO(mat: Material, opts: DownFacingAOOptions = {}): void {
  // Only works on materials that participate in the standard lighting model
  if (!(mat instanceof MeshStandardMaterial) && !(mat instanceof MeshPhysicalMaterial)) {
    return;
  }

  // Skip ALL transparent materials. AO darkens gl_FragColor which destroys
  // transparency and creates black edge artifacts on glass, headlight lenses,
  // tail-lights, and any other semi-transparent surface.
  if (mat.transparent) {
    return;
  }

  // Also skip transmissive materials (MeshPhysicalMaterial with transmission).
  if (mat instanceof MeshPhysicalMaterial && mat.transmission > 0) {
    return;
  }

  const strength = opts.strength ?? 0.8;
  const [r, g, b] = opts.color ?? [0.0, 0.0, 0.0];

  extendMaterialShader(mat, MOD_KEY, (shader) => {
    // ── Vertex Shader: compute AO factor ──────────────────────────────
    // Inject varying BEFORE <common> references it
    shader.vertexShader = shader.vertexShader.replace(
      '#include <common>',
      /* glsl */ `
        #include <common>
        varying float vDownAO;
      `,
    );

    // Compute dot(worldNormal, down) right after the normal is ready
    shader.vertexShader = shader.vertexShader.replace(
      '#include <beginnormal_vertex>',
      /* glsl */ `
        #include <beginnormal_vertex>
        vec3 worldNormalAO = normalize(normalMatrix * objectNormal);
        vDownAO = clamp(dot(worldNormalAO, vec3(0.0, -1.0, 0.0)), 0.0, 1.0);
      `,
    );

    // ── Fragment Shader: apply AO tint ────────────────────────────────
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <common>',
      /* glsl */ `
        #include <common>
        varying float vDownAO;
      `,
    );

    // Apply just before dithering (after tone-mapping, so the tint is
    // in display-referred space and composites predictably).
    // gl_FrontFacing guard: back faces (DoubleSide interior panels) must not
    // receive AO darkening — otherwise Z-fighting at overlapping body parts
    // causes the back face to render black and produces splotchy artefacts.
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <dithering_fragment>',
      /* glsl */ `
        if (gl_FrontFacing) {
          float aoFactor = vDownAO * ${strength.toFixed(4)};
          gl_FragColor.rgb = mix(gl_FragColor.rgb, vec3(${r.toFixed(4)}, ${g.toFixed(4)}, ${b.toFixed(4)}), aoFactor);
        }
        #include <dithering_fragment>
      `,
    );
  });
}

// ---------------------------------------------------------------------------
// Scene-level helper
// ---------------------------------------------------------------------------

/**
 * Traverse an entire scene graph and apply down-facing AO to every
 * MeshStandardMaterial / MeshPhysicalMaterial found on any mesh.
 *
 * Idempotent per-material — safe to call after MaterialManager.init()
 * has already swapped some materials.
 *
 * @param root The root Object3D to traverse (typically the cloned GLB scene).
 * @param opts Strength + debug colour overrides.
 */
export function applySceneAO(root: Object3D, opts: DownFacingAOOptions = {}): void {
  root.traverse((child) => {
    if (!(child instanceof Mesh)) return;
    const mats: Material[] = Array.isArray(child.material)
      ? child.material
      : child.material
        ? [child.material]
        : [];
    for (const mat of mats) {
      applyDownFacingAO(mat, opts);
    }
  });
}
